// src/components/typing/TypingArea.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { scoreWords } from "../../utils/wordScoring";

// ─── Marathi Inscript Keyboard Map ───────────────────────────────
const INSCRIPT_MAP = {
  q: "ौ", w: "ै", e: "ा", r: "ी", t: "ू", y: "ब", u: "ह", i: "ग", o: "द", p: "ज",
  "[": "ड", "]": "़", a: "ो", s: "े", d: "्", f: "ि", g: "ु", h: "प", j: "र", k: "क",
  l: "त", ";": "च", "'": "ट", z: "ॊ", x: "ं", c: "म", v: "न", b: "व", n: "ल", m: "स",
  ",": "य", ".": "।", "/": "॥",
  Q: "औ", W: "ऐ", E: "आ", R: "ई", T: "ऊ", Y: "भ", U: "ङ", I: "घ", O: "ध", P: "झ",
  "{": "ढ", "}": "ञ", A: "ओ", S: "ए", D: "अ", F: "इ", G: "उ", H: "फ", J: "ऱ", K: "ख",
  L: "थ", ":": "छ", '"': "ठ", Z: "ऑ", X: "ँ", C: "ण", V: "ञ", B: "ळ", N: "ऴ", M: "श",
  "<": "ऍ", ">": "ॉ", "?": "ॅ",
};

const toMarathi = (key) => INSCRIPT_MAP[key] || key;

const STATUS = {
  PENDING:   "pending",
  CORRECT:   "correct",
  INCORRECT: "incorrect",
  ACTIVE:    "active",
};

const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  ) || window.matchMedia("(pointer: coarse)").matches;

// ─── TypingArea ───────────────────────────────────────────────────
const TypingArea = ({
  text,
  timeLimit,
  language,
  mode,
  onStatsUpdate,
  onFinish,
  onRestart,
}) => {

  // ── State ──
  const [charMap,      setCharMap]      = useState([]);
  const [cursor,       setCursor]       = useState(0);
  const [keystrokes,   setKeystrokes]   = useState(0);
  const [timeLeft,     setTimeLeft]     = useState(timeLimit);
  const [timerActive,  setTimerActive]  = useState(false);
  const [started,      setStarted]      = useState(false);
  const [finished,     setFinished]     = useState(false);
  const [typedText,    setTypedText]    = useState("");
  const [isMobile,     setIsMobile]     = useState(false);

  // ── Test-mode state ──
  const [submitted,    setSubmitted]    = useState(false);
  const [testCharMap,  setTestCharMap]  = useState([]);
  const [rawTestInput, setRawTestInput] = useState("");

  // ── Refs ──
  const keystrokesRef    = useRef(0);
  const cursorRef        = useRef(0);
  const timeLeftRef      = useRef(timeLimit);
  const startTimeRef     = useRef(null);
  const textareaRef      = useRef(null);
  const displayRef       = useRef(null);
  const timerRef         = useRef(null);
  const activeCharRef    = useRef(null);
  const finishedRef      = useRef(false);
  const startedRef       = useRef(false);
  const prevValueRef     = useRef("");
  const charMapRef       = useRef([]);
  const rawTestRef       = useRef("");
  const charStatusesRef  = useRef([]);

  const isTest = mode === "test";

  // ─── Detect mobile ──────────────────────────────────────────────
  useEffect(() => { setIsMobile(isMobileDevice()); }, []);

  // ─── Initialize / Reset ─────────────────────────────────────────
  useEffect(() => {
    const map = text.split("").map((char) => ({ char, status: STATUS.PENDING }));
    if (map.length > 0) map[0].status = STATUS.ACTIVE;

    setCharMap(map);
    charMapRef.current      = map;
    setCursor(0);
    setKeystrokes(0);
    setTimeLeft(timeLimit);
    setTimerActive(false);
    setStarted(false);
    setFinished(false);
    setTypedText("");
    setSubmitted(false);
    setTestCharMap([]);
    setRawTestInput("");

    keystrokesRef.current   = 0;
    cursorRef.current       = 0;
    timeLeftRef.current     = timeLimit;
    finishedRef.current     = false;
    startedRef.current      = false;
    startTimeRef.current    = null;
    prevValueRef.current    = "";
    rawTestRef.current      = "";
    charStatusesRef.current = [];
  }, [text, timeLimit]);

  // ─── Auto-focus ─────────────────────────────────────────────────
  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
  }, [charMap.length]);

  // ─── Scroll active char into view ───────────────────────────────
  useEffect(() => {
    if (activeCharRef.current) {
      activeCharRef.current.scrollIntoView({
        block: "nearest", inline: "nearest", behavior: "smooth",
      });
    }
  }, [cursor]);

  // ─── Timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (timerActive && !finishedRef.current) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          const next = prev - 1;
          timeLeftRef.current = next;
          if (next <= 0) {
            clearInterval(timerRef.current);
            if (isTest) handleTestSubmit(true);
            else        handleFinish(true);
            return 0;
          }
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive]);

  // ─────────────────────────────────────────────────────────────────
  // pushStats — word-based accuracy
  // ─────────────────────────────────────────────────────────────────
  const pushStats = useCallback(
    (currentCursor, currentTimeLeft, currentCharMap, currentKeystrokes, currentTypedString) => {
      const elapsed = startTimeRef.current
        ? (Date.now() - startTimeRef.current) / 1000 / 60
        : 0;

      const correctChars = currentCharMap.filter(
        (c) => c.status === STATUS.CORRECT
      ).length;
      const wpm = elapsed > 0 ? Math.round(correctChars / 5 / elapsed) : 0;
      const cpm = elapsed > 0 ? Math.round(correctChars / elapsed) : 0;

      const ws = scoreWords(text, currentTypedString || "", false);

      charStatusesRef.current = ws.charStatuses || [];

      const progress = Math.round((currentCursor / text.length) * 100);

      onStatsUpdate({
        wpm,
        cpm,
        accuracy:       ws.accuracy,
        errors:         ws.wrongWords,
        keystrokes:     currentKeystrokes,
        timeLeft:       currentTimeLeft,
        progress,
        rightWords:     ws.rightWords,
        wrongWords:     ws.wrongWords,
        missedWords:    ws.missedWords,
        pendingWords:   ws.pendingWords,
        typedWordCount: ws.typedWordCount,
        totalWordCount: ws.totalWordCount,
      });
    },
    [text, onStatsUpdate]
  );

  // ─────────────────────────────────────────────────────────────────
  // handleFinish — practice mode
  // ─────────────────────────────────────────────────────────────────
  const handleFinish = useCallback(
    (timeUp = false) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      clearInterval(timerRef.current);
      setFinished(true);
      setTimerActive(false);

      const elapsed = startTimeRef.current
        ? (Date.now() - startTimeRef.current) / 1000 / 60
        : 0;
      const currentTimeLeft   = timeLeftRef.current;
      const currentKeystrokes = keystrokesRef.current;

      setCharMap((prev) => {
        const correctChars = prev.filter(
          (c) => c.status === STATUS.CORRECT
        ).length;
        const wpm = elapsed > 0 ? Math.round(correctChars / 5 / elapsed) : 0;
        const cpm = elapsed > 0 ? Math.round(correctChars / elapsed) : 0;

        const ws = scoreWords(text, prevValueRef.current || "", true);

        const wordBasedCharMap = prev.map((item, i) => ({
          char:   item.char,
          status: ws.charStatuses[i] ?? "pending",
        }));

        onFinish({
          wpm,
          cpm,
          accuracy:       ws.accuracy,
          errors:         ws.wrongWords,
          rightWords:     ws.rightWords,
          wrongWords:     ws.wrongWords,
          missedWords:    ws.missedWords,
          pendingWords:   ws.pendingWords,
          extraWords:     ws.extraWords,
          typedWordCount: ws.typedWordCount,
          totalWordCount: ws.totalWordCount,
          wordStatuses:   ws.wordStatuses,
          wordResults:    ws.wordResults,
          keystrokes:     currentKeystrokes,
          timeUsed:       timeLimit - (timeUp ? 0 : currentTimeLeft),
          totalChars:     text.length,
          correctChars,
          completedFully: !timeUp,
          charMap:        wordBasedCharMap,
        });
        return prev;
      });
    },
    [text, timeLimit, onFinish]
  );

  // ─────────────────────────────────────────────────────────────────
  // handleTestSubmit — test mode
  // ─────────────────────────────────────────────────────────────────
  const handleTestSubmit = useCallback(
    (timeUp = false) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      clearInterval(timerRef.current);
      setFinished(true);
      setTimerActive(false);
      setSubmitted(true);

      const raw = rawTestRef.current;

      const revealed = text.split("").map((char, i) => {
        const typed = raw[i];
        if (typed === undefined) return { char, status: STATUS.PENDING };
        return {
          char,
          status: typed === char ? STATUS.CORRECT : STATUS.INCORRECT,
        };
      });
      setTestCharMap(revealed);

      const elapsed = startTimeRef.current
        ? (Date.now() - startTimeRef.current) / 1000 / 60
        : 0;
      const currentTimeLeft   = timeLeftRef.current;
      const currentKeystrokes = keystrokesRef.current;

      const correctChars = revealed.filter(
        (c) => c.status === STATUS.CORRECT
      ).length;
      const wpm = elapsed > 0 ? Math.round(correctChars / 5 / elapsed) : 0;
      const cpm = elapsed > 0 ? Math.round(correctChars / elapsed) : 0;

      const ws = scoreWords(text, raw, true);

      const wordBasedCharMap = text.split("").map((char, i) => ({
        char,
        status: ws.charStatuses[i] ?? "pending",
      }));

      onFinish({
        wpm,
        cpm,
        accuracy:       ws.accuracy,
        errors:         ws.wrongWords,
        rightWords:     ws.rightWords,
        wrongWords:     ws.wrongWords,
        missedWords:    ws.missedWords,
        pendingWords:   ws.pendingWords,
        extraWords:     ws.extraWords,
        typedWordCount: ws.typedWordCount,
        totalWordCount: ws.totalWordCount,
        wordStatuses:   ws.wordStatuses,
        wordResults:    ws.wordResults,
        keystrokes:     currentKeystrokes,
        timeUsed:       timeLimit - (timeUp ? 0 : currentTimeLeft),
        totalChars:     text.length,
        correctChars,
        completedFully: !timeUp,
        charMap:        wordBasedCharMap,
      });
    },
    [text, timeLimit, onFinish]
  );

  // ─── Ensure timer starts on first keystroke ──────────────────────
  const ensureStarted = useCallback(() => {
    if (!startedRef.current) {
      startedRef.current   = true;
      startTimeRef.current = Date.now();
      setStarted(true);
      setTimerActive(true);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // processChar — practice mode
  // ─────────────────────────────────────────────────────────────────
  const processChar = useCallback(
    (typedChar) => {
      if (finishedRef.current) return;
      if (cursorRef.current >= text.length) return;
      ensureStarted();

      keystrokesRef.current += 1;
      setKeystrokes(keystrokesRef.current);

      const currentCursor  = cursorRef.current;
      const nextCursor     = currentCursor + 1;
      const expectedChar   = text[currentCursor];
      const isCorrect      = typedChar === expectedChar;
      const newTypedString = prevValueRef.current + typedChar;

      setCharMap((prev) => {
        const updated = [...prev];
        updated[currentCursor].status = isCorrect
          ? STATUS.CORRECT
          : STATUS.INCORRECT;
        if (nextCursor < updated.length) {
          updated[nextCursor].status = STATUS.ACTIVE;
        }
        charMapRef.current = updated;

        pushStats(
          nextCursor,
          timeLeftRef.current,
          updated,
          keystrokesRef.current,
          newTypedString
        );
        return updated;
      });

      cursorRef.current = nextCursor;
      setCursor(nextCursor);
      if (nextCursor >= text.length) handleFinish(false);
    },
    [text, ensureStarted, pushStats, handleFinish]
  );

  // ─────────────────────────────────────────────────────────────────
  // processBackspace — practice mode
  // ─────────────────────────────────────────────────────────────────
  const processBackspace = useCallback(() => {
    if (finishedRef.current) return;
    if (cursorRef.current === 0) return;

    keystrokesRef.current += 1;
    setKeystrokes(keystrokesRef.current);

    const currentCursor  = cursorRef.current;
    const prevCursor     = currentCursor - 1;
    const newTypedString = prevValueRef.current.slice(0, -1);

    setCharMap((prev) => {
      const updated = [...prev];
      updated[currentCursor].status = STATUS.PENDING;
      updated[prevCursor].status    = STATUS.ACTIVE;
      charMapRef.current = updated;

      pushStats(
        prevCursor,
        timeLeftRef.current,
        updated,
        keystrokesRef.current,
        newTypedString
      );
      return updated;
    });

    cursorRef.current = prevCursor;
    setCursor(prevCursor);
  }, [pushStats]);

  // ─────────────────────────────────────────────────────────────────
  // processTestChar — test mode
  // ─────────────────────────────────────────────────────────────────
  const processTestChar = useCallback(
    (typedChar) => {
      if (finishedRef.current) return;
      ensureStarted();

      keystrokesRef.current += 1;
      setKeystrokes(keystrokesRef.current);

      const pos          = rawTestRef.current.length;
      const expectedChar = text[pos];
      if (expectedChar === undefined) return;

      rawTestRef.current += typedChar;
      setRawTestInput(rawTestRef.current);

      const nextPos = rawTestRef.current.length;
      cursorRef.current = nextPos;
      setCursor(nextPos);

      const ws = scoreWords(text, rawTestRef.current, false);

      const elapsed = startTimeRef.current
        ? (Date.now() - startTimeRef.current) / 1000 / 60
        : 0;

      const correctSoFar = rawTestRef.current
        .split("")
        .filter((c, i) => c === text[i]).length;

      const wpm = elapsed > 0 ? Math.round(correctSoFar / 5 / elapsed) : 0;
      const cpm = elapsed > 0 ? Math.round(correctSoFar / elapsed) : 0;

      onStatsUpdate({
        wpm,
        cpm,
        accuracy:       ws.accuracy,
        errors:         ws.wrongWords,
        keystrokes:     keystrokesRef.current,
        timeLeft:       timeLeftRef.current,
        progress:       Math.round((nextPos / text.length) * 100),
        rightWords:     ws.rightWords,
        wrongWords:     ws.wrongWords,
        missedWords:    ws.missedWords,
        pendingWords:   ws.pendingWords,
        typedWordCount: ws.typedWordCount,
        totalWordCount: ws.totalWordCount,
      });

      if (nextPos >= text.length) handleTestSubmit(false);
    },
    [text, ensureStarted, onStatsUpdate, handleTestSubmit]
  );

  const processTestBackspace = useCallback(() => {
    // No backspace in test mode
  }, []);

  // ─── DESKTOP: onKeyDown ──────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e) => {
      if (finishedRef.current) return;
      if (
        ["Shift", "Control", "Alt", "Meta", "CapsLock", "Tab"].includes(e.key)
      ) {
        e.preventDefault();
        return;
      }
      if (e.key === "Unidentified" || e.key === undefined) return;

      e.preventDefault();

      if (e.key === "Backspace") {
        if (isTest) {
          processTestBackspace();
        } else {
          prevValueRef.current = prevValueRef.current.slice(0, -1);
          processBackspace();
          setTypedText((prev) => prev.slice(0, -1));
        }
        return;
      }

      if (e.key.length > 1) return;

      const typedChar =
        language === "marathi" ? toMarathi(e.key) : e.key;

      if (isTest) {
        processTestChar(typedChar);
        setTypedText((prev) => prev + typedChar);
        prevValueRef.current = prevValueRef.current + typedChar;
      } else {
        processChar(typedChar);
        setTypedText((prev) => prev + typedChar);
        prevValueRef.current = prevValueRef.current + typedChar;
      }
    },
    [
      language,
      isTest,
      processChar,
      processBackspace,
      processTestChar,
      processTestBackspace,
    ]
  );

  // ─── MOBILE: onChange ────────────────────────────────────────────
  const handleChange = useCallback(
    (e) => {
      if (!isMobile) return;
      if (finishedRef.current) return;

      const newValue = e.target.value;
      const oldValue = prevValueRef.current;

      if (newValue.length < oldValue.length) {
        if (!isTest) {
          const charsRemoved = oldValue.length - newValue.length;
          for (let i = 0; i < charsRemoved; i++) {
            prevValueRef.current = prevValueRef.current.slice(0, -1);
            processBackspace();
          }
        }
        prevValueRef.current = newValue;
        setTypedText(newValue);
        return;
      }

      if (newValue.length > oldValue.length) {
        const addedChars = newValue.slice(oldValue.length);
        for (const char of addedChars) {
          if (isTest) {
            processTestChar(char);
          } else {
            processChar(char);
          }
          prevValueRef.current = prevValueRef.current + char;
        }
        prevValueRef.current = newValue;
        setTypedText(newValue);
        return;
      }

      if (newValue !== oldValue) {
        let diffStart = 0;
        while (
          diffStart < oldValue.length &&
          newValue[diffStart] === oldValue[diffStart]
        ) {
          diffStart++;
        }
        if (!isTest) {
          const backspaceCount = oldValue.length - diffStart;
          for (let i = 0; i < backspaceCount; i++) {
            prevValueRef.current = prevValueRef.current.slice(0, -1);
            processBackspace();
          }
          const newChars = newValue.slice(diffStart);
          for (const char of newChars) {
            processChar(char);
            prevValueRef.current = prevValueRef.current + char;
          }
        }
        prevValueRef.current = newValue;
        setTypedText(newValue);
      }
    },
    [isMobile, isTest, processChar, processBackspace, processTestChar]
  );

  const handlePaste = (e) => e.preventDefault();

  // ─────────────────────────────────────────────────────────────────
  // renderPracticeChars — word-based coloring
  // ─────────────────────────────────────────────────────────────────
  const renderPracticeChars = () =>
    charMap.map((item, index) => {
      const wordStatus = charStatusesRef.current[index];
      const isActive   = item.status === STATUS.ACTIVE;

      let className = "typing-char";

      if (isActive) {
        className += " active";
      } else if (item.status === STATUS.PENDING) {
        if (wordStatus === "missed") className += " missed";
      } else {
        if      (wordStatus === "correct")   className += " correct";
        else if (wordStatus === "incorrect") className += " incorrect";
        else if (wordStatus === "missed")    className += " missed";
        else                                 className += " correct";
      }

      if (item.char === " ")
        return (
          <span
            key={index}
            className={`${className} space-char`}
            ref={isActive ? activeCharRef : null}
          >
            &nbsp;
          </span>
        );
      if (item.char === "\n")
        return (
          <span
            key={index}
            className={`${className} newline-char`}
            ref={isActive ? activeCharRef : null}
          >
            ↵<br />
          </span>
        );
      return (
        <span
          key={index}
          className={className}
          ref={isActive ? activeCharRef : null}
        >
          {item.char}
        </span>
      );
    });

  // ─── renderTestChars ─────────────────────────────────────────────
  const renderTestChars = () => {
    if (submitted && testCharMap.length > 0) {
      return testCharMap.map((item, index) => {
        let className = "typing-char";
        if (item.status === STATUS.CORRECT)        className += " correct";
        else if (item.status === STATUS.INCORRECT) className += " incorrect";
        else if (item.status === STATUS.PENDING)   className += " test-pending";

        if (item.char === " ")
          return (
            <span key={index} className={`${className} space-char`}>
              &nbsp;
            </span>
          );
        if (item.char === "\n")
          return (
            <span key={index} className={`${className} newline-char`}>
              ↵<br />
            </span>
          );
        return (
          <span key={index} className={className}>
            {item.char}
          </span>
        );
      });
    }

    return text.split("").map((char, index) => {
      const isCurrent = index === cursorRef.current;
      let className   = "typing-char test-plain";
      if (isCurrent) className += " active";

      if (char === " ")
        return (
          <span
            key={index}
            className={`${className} space-char`}
            ref={isCurrent ? activeCharRef : null}
          >
            &nbsp;
          </span>
        );
      if (char === "\n")
        return (
          <span
            key={index}
            className={`${className} newline-char`}
            ref={isCurrent ? activeCharRef : null}
          >
            ↵<br />
          </span>
        );
      return (
        <span
          key={index}
          className={className}
          ref={isCurrent ? activeCharRef : null}
        >
          {char}
        </span>
      );
    });
  };

  const progressPercent = Math.round(
    ((isTest ? rawTestInput.length : cursor) / text.length) * 100
  );

  // ─── JSX ─────────────────────────────────────────────────────────
  return (
    <div className="typing-area-wrapper">

      {/* ── Mode Badge ── */}
      <div className="typing-mode-badge-row">
        <span className={`typing-mode-badge ${isTest ? "test" : "practice"}`}>
          {isTest ? "🧪 Test Mode" : "🏋️ Practice Mode"}
        </span>
        {isTest && !submitted && (
          <span className="test-mode-rules">
            No backspace • No error hints • Submit when done
          </span>
        )}
      </div>

      {/* ── Progress Bar ── */}
      <div className="typing-progress-bar-track">
        <div
          className="typing-progress-bar-fill"
          style={{ width: `${progressPercent}%` }}
        />
        <span className="typing-progress-label">{progressPercent}%</span>
      </div>

      {/* ── Text Display Box ── */}
      <div
        ref={displayRef}
        className={`
          typing-display
          ${isTest ? "test-mode-display" : ""}
          ${language === "marathi" ? "marathi-font" : ""}
          ${finished ? "finished" : ""}
          ${submitted ? "submitted" : ""}
        `}
        aria-label="Text to type"
      >
        {charMap.length > 0
          ? isTest
            ? renderTestChars()
            : renderPracticeChars()
          : <span className="typing-placeholder">Loading text...</span>
        }
      </div>

      {/* ── Post-submit legend (test mode) ── */}
      {isTest && submitted && (
        <div className="test-submit-legend">
          <span className="legend-dot correct">● Correct</span>
          <span className="legend-dot incorrect">● Incorrect</span>
          <span className="legend-dot pending">● Not reached</span>
        </div>
      )}

      {/* ── Helper Text ── */}
      {!started && !finished && (
        <p className="typing-helper-text">
          {isMobile
            ? "📱 Tap the input below and start typing!"
            : isTest
            ? "⌨️ Start typing — timer starts on your first keystroke. No backspace allowed!"
            : "👆 Click below and start typing — timer starts on your first keystroke!"}
        </p>
      )}

      {/* ── Typing Input Box ── */}
      <div className="typing-input-wrapper">
        <textarea
          ref={textareaRef}
          className={`
            typing-textarea
            ${isTest ? "test-mode-textarea" : ""}
            ${language === "marathi" ? "marathi-font" : ""}
          `}
          value={typedText}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onPaste={handlePaste}
          onCopy={handlePaste}
          onCut={handlePaste}
          disabled={finished}
          placeholder={
            finished
              ? isTest
                ? submitted
                  ? "✅ Submitted! See results above."
                  : "⏰ Time's up! Results revealed above."
                : cursor >= text.length
                ? "🎉 You completed the text!"
                : "⏰ Time's up!"
              : started
              ? isTest
                ? "Keep typing... (no backspace)"
                : "Keep typing..."
              : isMobile
              ? "Tap here and start typing..."
              : "Click here and start typing..."
          }
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          inputMode="text"
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          aria-label="Typing input area"
        />

        <div className="typing-input-footer">
          <span className="typing-char-count">
            {isTest ? rawTestInput.length : cursor} / {text.length} characters
          </span>
          {started && !finished && (
            <span className="typing-input-hint">
              {isTest
                ? "🚫 No backspace • 🚫 No paste"
                : isMobile
                ? "Type carefully — autocorrect is off"
                : "Paste disabled • Backspace to correct"}
            </span>
          )}
        </div>
      </div>

      {/* ── Action Buttons ── */}
      <div className="typing-action-row">
        {isTest && started && !finished && (
          <button
            className="typing-submit-btn"
            onClick={() => handleTestSubmit(false)}
          >
            ✅ Submit Test
          </button>
        )}
        {started && (
          <button className="typing-restart-btn" onClick={onRestart}>
            🔄 Restart
          </button>
        )}
      </div>

      {/* ── Marathi Hint ── */}
      {language === "marathi" && !finished && (
        <div className="marathi-keyboard-hint">
          <span className="hint-icon">⌨️</span>
          <span className="hint-text">
            {isMobile
              ? "Switch your keyboard to Marathi (Devanagari) to type in Marathi."
              : (
                <>
                  Using <strong>Inscript</strong> layout. Keep system input on{" "}
                  <strong>English</strong> — app converts automatically.
                </>
              )}
          </span>
        </div>
      )}

      {/* ── Finished Overlay ── */}
      {finished && (
        <div
          className={`typing-finished-overlay ${
            isTest && submitted ? "test-submitted" : ""
          }`}
        >
          <span className="finished-icon">{isTest ? "📋" : "🎉"}</span>
          <span className="finished-text">
            {isTest
              ? "Test submitted! Scroll up to see your results."
              : cursor >= text.length
              ? "You completed the text!"
              : "Time's up!"}
          </span>
        </div>
      )}

    </div>
  );
};

export default TypingArea;